// InteliChat #41 - the shared URGENCY MODEL (single source of truth).
//
// "Urgent now" is the whole point of the never-miss HUD, so exactly ONE
// definition of it lives here and the composable (membership + sort), the
// HUD rows (label + visual ramp) and the Today hero (headline + subline)
// all read from it. Change a threshold once, everything agrees.
//
// The spine (charlie's spec): urgency = (is the customer HERE right now?)
// x (how long have they WAITED unanswered?). A present customer waiting a
// few minutes = act now. A customer who left, or who has been unanswered
// past the window = missed: follow up, not a live emergency.

// A still-present (ONLINE) customer NO LONGER drops to MISSED at this wait -
// we keep alerting an actively-waiting customer indefinitely (Tyler
// 2026-07-13). A chat becomes MISSED only when the customer goes OFFLINE
// (leaves) while unanswered. This constant now only tunes the "about to
// slip" wording (see SLIP_COPY_S), not act-now membership.
export const MISS_THRESHOLD_S = 15 * 60;

// A customer who messaged this recently is treated as HERE even if the
// presence broadcast has not landed yet - someone who wrote 90s ago is
// almost certainly still on the page. Keeps a genuinely-fresh chat in the
// act-now buckets instead of hiding it in MISSED on a laggy presence feed
// (the "never miss" mandate; see the data-signal note in the report).
export const PRESENCE_GRACE_S = 120;

// Escalation ramp inside the act-now buckets (NEEDS YOU NOW / NEW): the
// row visibly intensifies with the wait. calm < 2m, warm 2-5m, hot 5-6m,
// slip 6m+ (about to slip toward the miss threshold).
export const WARM_S = 120;
export const HOT_S = 300;
export const SLIP_S = 360;

// The wait at which a still-unanswered act-now row earns the explicit
// "about to slip" wording - reserved for NEAR the miss threshold (the
// last few minutes before it drops to MISSED), distinct from the colour
// ramp above which peaks earlier. Spec: 6 min reads "reply now", 11 min
// reads "about to slip".
export const SLIP_COPY_S = MISS_THRESHOLD_S - 5 * 60;

// Seconds a customer has been waiting (0 when answered / not waiting).
export const waitSeconds = (waitingSince, now) => {
  const since = Number(waitingSince || 0);
  if (!since) return 0;
  const ref = Number(now) || Math.floor(Date.now() / 1000);
  return Math.max(0, ref - since);
};

// Still inside the act-now window (recent enough to be a live emergency).
export const isRecentWait = (waitingSince, now) => {
  const since = Number(waitingSince || 0);
  return since > 0 && waitSeconds(since, now) <= MISS_THRESHOLD_S;
};

// The single, pure bucket decision - the load-bearing membership rule.
// Used by useUrgencyBuckets AND covered directly by the logic tests, so
// there is exactly one definition of "urgent now". Returns one of
// 'needsYouNow' | 'new' | 'missed' | null (null = replied, not in the HUD).
//   signals = { awaitingReply, waitingSince, online, unassigned }
export const classifyBucket = (signals, now) => {
  if (!signals || !signals.awaitingReply) return null; // you replied -> gone
  const secs = waitSeconds(signals.waitingSince, now);
  // HERE = online per the feed, OR messaged within the grace window (a
  // just-arrived chat is urgent before the presence broadcast lands).
  const here = signals.online || secs <= PRESENCE_GRACE_S;
  // A customer still HERE (active on the site) stays act-now no matter how
  // long they have waited - never stop alerting a live waiting customer
  // (Tyler 2026-07-13). Only a customer who has LEFT (offline, past the
  // just-arrived grace) drops to MISSED: a follow-up, not a live emergency.
  if (!here) return 'missed';
  return signals.unassigned ? 'new' : 'needsYouNow';
};

// The escalation-ramp stage for a live, act-now row.
export const stageFor = secs => {
  if (secs >= SLIP_S) return 'slip';
  if (secs >= HOT_S) return 'hot';
  if (secs >= WARM_S) return 'warm';
  return 'calm';
};

// Compact human wait: "45 sec", "6 min", "2 hr". Leads the microcopy so
// the decision fact (how long they have waited) is read first.
export const humanWait = secs => {
  const s = Math.max(0, Math.floor(secs));
  if (s < 60) return `${s} sec`;
  const mins = Math.floor(s / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  return `${hrs} hr`;
};

// The per-conversation line for a HUD row / hero subline. Presence + wait
// lead; wording is imperative and specific (charlie's prose rules). No em
// dashes: hyphens and colons only.
export const rowMicrocopy = (bucket, online, waitingSince, now) => {
  const secs = waitSeconds(waitingSince, now);
  if (bucket === 'missed') {
    return online
      ? 'No reply yet - reach back out'
      : `No reply yet - left ${humanWait(secs)} ago`;
  }
  if (bucket === 'new') {
    if (secs < WARM_S) return online ? 'Just came in - online now' : 'Just came in';
    return `Waiting ${humanWait(secs)} - still unclaimed`;
  }
  // needsYouNow
  if (secs >= SLIP_COPY_S) return `Waiting ${humanWait(secs)} - about to slip`;
  return `Waiting ${humanWait(secs)} - reply now`;
};
